import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from './../../utils/api';
import { numberFormat } from './../../utils/functions';
import './BalancePayCard.css';

/**
 * กล่อง "ยอดคงเหลือ + ปุ่มชำระเงินออนไลน์" บนหน้าสถานะการจอง
 *
 * หน้านี้คือปลายทางของทั้งลิงก์ใน SMS ก่อนเช็คอิน และ QR ที่พนักงานให้ลูกค้าสแกนที่หน้างาน
 * เซิร์ฟเวอร์เป็นคนตัดสินว่าจ่ายออนไลน์ได้ไหม (ที่พักใช้ gateway, เปิดให้จ่ายยอดคงเหลือออนไลน์,
 * จ่ายรอบแรกแล้ว, ยังมียอดค้าง) -- ไม่ได้ก็ไม่แสดงอะไรเลย หน้าสถานะเลยเหมือนเดิมทุกอย่าง
 * สำหรับที่พักโอนตรงและการจองที่จ่ายครบแล้ว
 *
 * มีงวดที่แอดมินแบ่งไว้ (reason = payment_request_open) -> แสดงปุ่มจ่ายของแต่ละงวดแทน
 *
 * props: type = 'booking' | 'main' | 'cart', id, code (เลขจอง / เลขตะกร้า)
 */
export default function BalancePayCard({ type, id, code }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);

  useEffect(() => {
    if (!id || !code) return;
    const idField = type === 'cart' ? 'booking_cart_id' : (type === 'main' ? 'booking_main_id' : 'booking_id');
    api.request('omise-payments/balance', (res, success) => {
      setInfo(success ? res : null);
    }, { [idField]: id, code }, 'GET', { showError: false });
  }, [type, id, code]);

  // จองตอนที่พักยังรับโอนตรง สลิปมัดจำยังไม่ได้ตรวจ -- บอกว่าทำไมยังไม่มีปุ่มจ่าย
  if (info && info.reason === 'deposit_slip_pending') {
    return (
      <div className="balance-pay balance-pay--pending">
        <div className="balance-pay__info">
          <div className="balance-pay__label">{t('balance_card_title')}</div>
          <div className="balance-pay__amount">฿{numberFormat(info.amount, true)}</div>
          <div className="balance-pay__note">{t('balance_slip_pending')}</div>
        </div>
      </div>
    );
  }

  // แอดมินแบ่งงวดไว้ -- ปุ่มยอดคงเหลือทั้งก้อนถูกปิด ชี้ไปงวดที่ต้องจ่ายแทน (กันจ่ายซ้ำสองลิงก์)
  if (info && info.reason === 'payment_request_open' && Array.isArray(info.requests) && info.requests.length) {
    return (
      <>
        {info.requests.map((r) => {
          let path = '';
          try {
            path = new URL(r.pay_url).pathname;
          } catch (e) {
            return null;
          }
          return (
            <div className="balance-pay" key={r.id}>
              <div className="balance-pay__info">
                <div className="balance-pay__label">{r.label || t('request_open_title')}</div>
                <div className="balance-pay__amount">฿{numberFormat(r.amount, true)}</div>
                {r.due_at && (
                  <div className="balance-pay__note">
                    {t('request_due', { date: new Date(r.due_at).toLocaleString('th-TH', { dateStyle: 'medium', timeStyle: 'short' }) })}
                  </div>
                )}
              </div>
              <button type="button" className="balance-pay__btn" onClick={() => navigate(path)}>
                {t('balance_pay_online')}
                <span className="balance-pay__btn-hint">{t('balance_pay_online_hint')}</span>
              </button>
            </div>
          );
        })}
      </>
    );
  }

  if (!info || !info.payable) return null;

  const pay = () => navigate(`/once/payment/${type}/${id}?purpose=balance&code=${encodeURIComponent(code)}`);

  return (
    <div className="balance-pay">
      <div className="balance-pay__info">
        <div className="balance-pay__label">{t('balance_card_title')}</div>
        <div className="balance-pay__amount">฿{numberFormat(info.amount, true)}</div>
        <div className="balance-pay__note">
          {info.mode === 'online' ? t('balance_must_pay_online') : t('balance_or_pay_on_stay')}
        </div>
      </div>
      <button type="button" className="balance-pay__btn" onClick={pay}>
        {t('balance_pay_online')}
        <span className="balance-pay__btn-hint">{t('balance_pay_online_hint')}</span>
      </button>
    </div>
  );
}
